import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Flex, Image } from '@chatterbug/aaron'
import { t } from 'src/lib/i18n-react'

import cross from 'src/ui/assets/icons/cross.svg'
import ConfirmationModal from 'src/ui/Modals/ConfirmationModal/ConfirmationModal'
import rightArrow from 'src/ui/assets/icons/right-arrow.svg'
import dragHandle from 'src/ui/assets/icons/drag-handle.svg'

import UnitListItemInfo from '../UnitListItemInfo/UnitListItemInfo'

import CurrentUnitIndicator, {
  INDICATOR_SHAPE_POSITION_STYLES,
  INDICATOR_SHAPE_POSITION_STYLES_ABOVE_ALL,
  INDICATOR_BORDER_STYLES,
} from './subcomponents/CurrentUnitIndicator/CurrentUnitIndicator'
import ProgressIcons from './subcomponents/ProgressIcons/ProgressIcons'

const INITIAL_MODAL_PROPS = {
  isOpen: false,
}

const getRemoveUnitModalProps = (name) => ({
  title: t('Remove %{name}', { name }),
  message: t(
    "Don't worry, we will save your progress. You can add this unit back to your course at anytime."
  ),
  confirmButtonLabel: t('Remove'),
})

const checkIfSelfStudyCompleted = (selfStudyItem) => {
  return ['seen', 'mastered', 'verified'].indexOf(selfStudyItem) >= 0
}

const UnitListItem = ({
  name,
  label,
  topic,
  progress,
  iconUrl,
  state,
  draggable,
  removable,
  inCoachMarkSpotlight,
  onRemove,
}) => {
  const [modalProps, setModalProps] = useState(INITIAL_MODAL_PROPS)

  const hideUnitData = Boolean(state) // for Topic Detail page
  const showCurrentIndicator = !hideUnitData && progress?.isCurrent
  const isQueueable = topic?.isQueueable
  const topicName = isQueueable
    ? topic?.name
    : t('Unit %{unitNum} • %{unitName}', {
        unitNum: label,
        unitName: topic?.name,
      })

  const handleConfirmRemove = () => {
    setModalProps({
      ...getRemoveUnitModalProps(name),
      isOpen: true,
      onConfirm: () => {
        onRemove()
        setModalProps(INITIAL_MODAL_PROPS)
      },
      onHide: () => setModalProps(INITIAL_MODAL_PROPS),
    })
  }

  return (
    <Box>
      {showCurrentIndicator && (
        <CurrentUnitIndicator
          sx={
            inCoachMarkSpotlight
              ? INDICATOR_SHAPE_POSITION_STYLES_ABOVE_ALL
              : INDICATOR_SHAPE_POSITION_STYLES
          }
        />
      )}
      <Box
        width="100%"
        color="black0"
        borderRadius="1x"
        boxShadow="raised"
        p="2x"
        pr={{ _: '2x', tablet: '56px' }} // to account for the "arrow" background image.
        backgroundImage={
          removable ? 'none' : { _: 'none', tablet: `url(${rightArrow})` }
        }
        sx={{
          backgroundColor: 'white100',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 16px top 50%',
          listStyleType: 'none',
          position: 'relative',
          overflow: 'hidden',
          ':active': {
            boxShadow: 'pressed',
          },
        }}
      >
        <Box
          width="100%"
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            ...(showCurrentIndicator ? INDICATOR_BORDER_STYLES : {}),
          }}
        />
        <Flex justifyContent="space-between" alignItems="center">
          {draggable && (
            <Image src={dragHandle} width={24} height={24} mr="1x" />
          )}
          <Flex
            flexDirection={{ _: 'column', tablet: 'row' }}
            flex={1}
            alignItems={{ _: 'normal', tablet: 'center' }}
          >
            {/* includes LL progress bar */}
            <UnitListItemInfo
              name={name}
              topicName={!hideUnitData && topicName}
              iconUrl={iconUrl}
              progress={progress}
              showProgressBar={!hideUnitData && Boolean(progress)}
            />
            {!hideUnitData && progress && (
              <ProgressIcons
                // vocabulary is always present for a unit
                vocab={{
                  isCompleted: checkIfSelfStudyCompleted(progress?.flashcards),
                }}
                writing={{
                  isPresent: Boolean(progress?.writing),
                  isCompleted: checkIfSelfStudyCompleted(progress?.writing),
                }}
                media={{
                  isPresent: Boolean(progress?.media),
                  isCompleted: checkIfSelfStudyCompleted(progress?.media),
                }}
                concepts={{
                  isPresent: Boolean(progress?.concepts),
                  isCompleted: checkIfSelfStudyCompleted(progress?.concepts),
                }}
              />
            )}
          </Flex>
          {state}
        </Flex>
        {removable && (
          <>
            <Box
              as="button"
              onClick={handleConfirmRemove}
              sx={{
                position: 'absolute',
                top: '1x',
                right: '1x',
                background: 'none',
                border: 'none',
              }}
            >
              <Image src={cross} alt={t('Remove')} />
            </Box>
            {modalProps.isOpen && <ConfirmationModal {...modalProps} />}
          </>
        )}
      </Box>
    </Box>
  )
}

UnitListItem.propTypes = {
  id: PropTypes.string,
  topic: PropTypes.shape({
    name: PropTypes.string,
    isQueueable: PropTypes.bool,
  }),
  progress: PropTypes.shape({
    isQueued: PropTypes.bool,
    isCurrent: PropTypes.bool,
    isCompleted: PropTypes.bool,
    isSkipped: PropTypes.bool,
    exercises: PropTypes.string,
    percentOfExercisesSeen: PropTypes.number,
    flashcards: PropTypes.string,
    media: PropTypes.string,
    writing: PropTypes.string,
    concepts: PropTypes.string,
  }),
  iconUrl: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  state: PropTypes.any,
  draggable: PropTypes.bool,
  removable: PropTypes.bool,
  inCoachMarkSpotlight: PropTypes.bool,
  onRemove: PropTypes.func,
}

export default UnitListItem
